import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Lightbox from 'yet-another-react-lightbox';
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { imageFiles } from '../data/imageList';

const GallerySectionContainer = styled.div`
  min-height: 100vh;
  padding: 80px 20px;
  color: white;
`;

const GalleryTitle = styled.h2`
  font-family: 'GeneralSans', sans-serif;
  font-size: clamp(42px, 6vw, 64px);
  font-weight: 400;
  margin-bottom: 40px;
  text-align: center;
  color: white;
`;

const GallerySubtitle = styled.div`
  font-family: 'GeneralSans', sans-serif;
  font-size: 21px;
  font-weight: 500;
  text-align: center;
  margin-bottom: 60px;
  color: rgba(255, 255, 255, 0.8);
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 20px;
  padding: 20px;
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;

  @media (max-width: 1600px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
    padding: 15px;
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(1, 1fr);
    gap: 10px;
    padding: 10px;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DownloadButton = styled.button`
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 1;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
`;

const DownloadIcon = () => (
  <svg 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const DownloadButtonLightbox = styled.button`
  background: none;
  color: white;
  border: none;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const DownloadAllButton = styled.button`
  background: transparent;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.8);
  padding: 16px 48px;
  font-size: 18px;
  font-weight: 500;
  border-radius: 100px;
  cursor: pointer;
  font-family: 'GeneralSans', sans-serif;
  margin: 0 auto 40px;
  display: block;
  transition: all 0.3s ease;
  letter-spacing: 0.2px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const VideoContainer = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto 40px;
  position: relative;
  aspect-ratio: 1920/1080;
  background: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  @media (max-width: 900px) {
    padding: 0;
  }

  @media (max-width: 600px) {
    padding: 0;
  }
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

interface GalleryImage {
  src: string;
  thumbnail: string;
  title: string;
}

const GallerySection: React.FC = () => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [index, setIndex] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadInProgress, setDownloadInProgress] = useState(false);
  const [showThumbnails, setShowThumbnails] = useState(true);

  useEffect(() => {
    const loadImages = async () => {
      try {
        const loadedImages = imageFiles.map((filename: string) => ({
          src: `/gallery/web/${encodeURIComponent(filename)}`,
          thumbnail: `/gallery/web/${encodeURIComponent(filename)}`,
          title: filename,
        }));

        setImages(loadedImages);
        setError(null);
      } catch (err) {
        console.error('Error loading images:', err);
        setError('Nepavyko užkrauti nuotraukų');
      } finally {
        setLoading(false);
      }
    };

    loadImages();
  }, []);

  const handleDownloadAll = async () => {
    if (downloadInProgress) return;
    
    setDownloadInProgress(true);
    const zip = new JSZip();
    
    try {
      for (const image of images) {
        const response = await fetch(image.src);
        if (!response.ok) continue;
        const blob = await response.blob();
        const filename = decodeURIComponent(image.src.split('/').pop() || 'image.jpg');
        zip.file(filename, blob);
      }
      
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, 'juodeliai-30-nuotraukos.zip');
    } catch (error) {
      console.error('Error downloading images:', error);
      setError('Nepavyko atsisiųsti nuotraukų. Bandykite dar kartą.');
    } finally {
      setDownloadInProgress(false);
    }
  };

  const handleDownloadSingle = async (imageSrc: string, title: string) => {
    try {
      const response = await fetch(imageSrc);
      const blob = await response.blob();
      saveAs(blob, title);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  if (loading) {
    return (
      <GallerySectionContainer>
        <GalleryTitle>Judam toliau!</GalleryTitle>        <GallerySubtitle>Prašome palaukti...</GallerySubtitle>
      </GallerySectionContainer>
    );
  }

  if (error) {
    return (
      <GallerySectionContainer>
        <GalleryTitle>Judam toliau!</GalleryTitle>
        <GallerySubtitle style={{ color: '#ff6b6b' }}>{error}</GallerySubtitle>
        <DownloadButton onClick={() => window.location.reload()}>
          Bandyti dar kartą
        </DownloadButton>
      </GallerySectionContainer>
    );
  }

  return (
    <GallerySectionContainer>
      <GalleryTitle>Judam toliau!</GalleryTitle>
      
      <VideoContainer>
        <Video
          controls
          playsInline
          src="/id4k.mp4"
        >
          <source src="/id4k.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </Video>
      </VideoContainer>

      <DownloadAllButton 
        onClick={handleDownloadAll} 
        disabled={downloadInProgress}
      >
        {downloadInProgress ? 'Ruošiamas parsisiuntimas...' : 'Parsisiųsti visas nuotraukas'}
      </DownloadAllButton>

      <GalleryGrid>
        {images.map((image, index) => (
          <ImageContainer key={image.src}>
            <img
              src={image.src}
              alt={image.title}
              loading="lazy"
              onClick={() => setIndex(index)}
              style={{ opacity: error ? 0.5 : 1 }}
            />
          </ImageContainer>
        ))}
      </GalleryGrid>

      <Lightbox
        open={index >= 0}
        index={index}
        close={() => setIndex(-1)}
        slides={images}
        plugins={[Thumbnails]}
        carousel={{
          finite: true,
          spacing: 0,
          padding: 0
        }}
        animation={{
          fade: 150,
          swipe: 0
        }}
        styles={{
          container: { backgroundColor: "rgba(0, 0, 0, .9)" },
          slide: { transition: "opacity 150ms ease" },
          thumbnailsTrack: { 
            gap: "16px",
            padding: "0 16px",
            backgroundColor: "transparent"
          },
          thumbnail: {
            border: "none",
            borderRadius: 0,
            transition: "transform 0.2s ease",
            "--yarl__thumbnails_selected_size": "1.3",
            backgroundColor: "transparent"
          },
          thumbnailsContainer: {
            backgroundColor: "rgba(0, 0, 0, .9)"
          }
        }}
        thumbnails={{
          position: "bottom",
          width: 120,
          height: 80,
          border: 0,
          borderRadius: 0,
          padding: 4,
          gap: 16,
          showToggle: true,
          vignette: false
        }}
        toolbar={{
          buttons: [
            'thumbnails',
            <DownloadButtonLightbox
              key="download"
              onClick={() => index >= 0 && handleDownloadSingle(images[index].src, images[index].title)}
              title="Parsisiųsti"
            >
              <DownloadIcon />
            </DownloadButtonLightbox>,
            'close'
          ]
        }}
      />
    </GallerySectionContainer>
  );
};

export default GallerySection;
